import React, {useEffect, useLayoutEffect, useRef} from 'react';
import {useVideoTracking} from 'app/components/videoEmbeeds/useVideoTracking';
import {updateYTScriptLoaded} from 'app/actions/embedActions';
import {useDispatch, useSelector} from 'react-redux';
import {createSelector} from 'reselect';

const youtubeScriptId = 'youtube-embed';

const initYoutubeScript = () => {
  if (!document.getElementById(youtubeScriptId)) {
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.type = 'text/javascript';
    script.id = youtubeScriptId;
    document.head.appendChild(script);
  }
};

const selectYTScriptLoaded = createSelector(
  state => state.embeds,
  embeds => embeds.ytScriptLoaded
);

const VideoEmbedYoutube = ({isHydrationFinished, id, source, matchSlug, open, playlistId, videoUrl}) => {
  const {trackEvent, setTrackEvent} = useVideoTracking(source, matchSlug, playlistId, videoUrl);
  const ytScriptLoaded = useSelector(selectYTScriptLoaded);
  const dispatch = useDispatch();
  const playerRef = useRef(null);

  useEffect(() => {
    if (isHydrationFinished && !window.YT) {
      initYoutubeScript();
    }
  }, [isHydrationFinished]);

  useEffect(() => {
    const player = playerRef.current;
    if (player && !open) {
      // destroy player on overlay close
      setTrackEvent(false);
      if (player?.stopVideo && typeof player.stopVideo === 'function') {
        player.stopVideo();
      }
      player.destroy();
      playerRef.current = null;
    } else if (!player && ytScriptLoaded && open) {
      // create player on overlay open
      initPlayer();
    }
  }, [open]);

  useLayoutEffect(() => {
    // Check if Youtube API Script is already loaded and display video directly
    if (window.YT && window.onYouTubeIframeAPIReady) {
      window.YT.ready(() => dispatch(updateYTScriptLoaded(true)));
    } else {
      window.onYouTubeIframeAPIReady = () => dispatch(updateYTScriptLoaded(true));
    }
  }, []);

  useEffect(() => {
    if (ytScriptLoaded) {
      // initially create player
      initPlayer();
    }
  }, [ytScriptLoaded]);

  const initPlayer = () => {
    playerRef.current = new YT.Player('ytplayer-' + id, {
      height: '100%',
      width: '100%',
      videoId: id,
      playerVars: {'allow-presentation': true},
      events: {
        onStateChange: function (event) {
          if (event.data === window.YT.PlayerState.PLAYING && !trackEvent) {
            setTrackEvent(true);
          }
        },
      },
    });
  };

  return <div id={`ytplayer-${id}`} />;
};

export {VideoEmbedYoutube};
